import { api } from './client';
import { i18nMetaMap } from '@/lib/i18n-map';
import '@/lib/i18n-map';

export const WAITLIST_STATUSES = ['waiting', 'contacted', 'booked', 'dropped'] as const;
export type WaitlistStatus = (typeof WAITLIST_STATUSES)[number];

export const WAITLIST_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type WaitlistPriority = (typeof WAITLIST_PRIORITIES)[number];

export const WAITLIST_STATUS_META = i18nMetaMap<
  WaitlistStatus,
  { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' }
>('common', {
  waiting: { label: 'inAsteptare', tone: 'warn' },
  contacted: { label: 'contactat', tone: 'info' },
  booked: { label: 'programat', tone: 'ok' },
  dropped: { label: 'renuntat', tone: 'neutral' },
});

export const WAITLIST_PRIORITY_META = i18nMetaMap<WaitlistPriority, { label: string }>('common', {
  low: { label: 'scazuta' },
  normal: { label: 'normala' },
  high: { label: 'ridicata' },
  urgent: { label: 'urgenta2' },
});

export interface WaitlistEntry {
  id: string;
  patientName: string;
  phone: string | null;
  patientId: string | null;
  service: string | null;
  doctorId: string | null;
  doctorName: string | null;
  priority: WaitlistPriority;
  availability: string | null;
  note: string | null;
  status: WaitlistStatus;
  createdAt: string;
}

export interface WaitlistSummary {
  waiting: number;
  contacted: number;
  booked: number;
  dropped: number;
  urgent: number;
}

/**
 * Written out rather than derived from `WaitlistEntry`: validation runs with
 * `forbidNonWhitelisted`, so sending back `id`, `doctorName` or `createdAt`
 * would fail the whole request instead of being ignored.
 */
export interface CreateWaitlistInput {
  patientName: string;
  phone?: string | null;
  patientId?: string | null;
  service?: string | null;
  doctorId?: string | null;
  priority?: WaitlistPriority;
  availability?: string | null;
  note?: string | null;
}

export interface UpdateWaitlistInput extends Partial<CreateWaitlistInput> {
  status?: WaitlistStatus;
}

export const waitlistApi = {
  list: (query: { status?: WaitlistStatus; all?: boolean; limit?: number; offset?: number } = {}) =>
    api.get<WaitlistEntry[]>('/waitlist', { query }),
  summary: () => api.get<WaitlistSummary>('/waitlist/summary'),
  /** Who a slot freed in this doctor's day could be offered to, best first. */
  candidates: (query: { doctorId?: string | null; limit?: number } = {}) =>
    api.get<WaitlistEntry[]>('/waitlist/candidates', {
      query: { doctorId: query.doctorId ?? undefined, limit: query.limit },
    }),
  create: (body: CreateWaitlistInput) => api.post<WaitlistEntry>('/waitlist', body),
  update: (id: string, body: UpdateWaitlistInput) =>
    api.patch<WaitlistEntry>(`/waitlist/${id}`, body),
  remove: (id: string) => api.delete<void>(`/waitlist/${id}`),
};
